import { practiceAction } from './pilot_actions';
import { EXTENSION_CELLS, SANDBOX_IDS } from './practice_pools';
/**
 * practice.ts — resettable practice cells for the Cut & Merge tutorial.
 *
 * Schema: supabase-tutorial-practice-schema.sql. One example goes to one
 * user at a time. Two kinds: `merge_then_cut` (a merge example: B starts
 * wrongly disconnected and the learner merges it onto A) and `cut` (A and B
 * start wrongly fused and the learner cuts them apart). The kind name is
 * historical; the reset undoes whatever the learner did either way.
 * When they finish or leave, every PyChunkedGraph operation made on the
 * example since its baseline is undone, newest first, with the user's own
 * CAVE token, and the example goes back to `ready` with refreshed roots.
 * If that fails, or the tab just closes, the example is marked
 * `needs_reset` (or its claim expires) and scripts/reset-practice-examples.mjs
 * does the same undo with the service token.
 *
 * Supervoxels are the durable identity of the two pieces; roots are looked
 * up from them whenever the example is handed out.
 */
import { Uint64 } from 'neuroglancer/util/uint64';
import { setStatedColor } from './widgets/widget_utils';
import { supabase } from './supabase';
import { practiceBase, practiceToken } from './util/practice_destination';
import { practiceOperationsAfter, remainingPracticeOperations } from './util/practice_history';
export { parsePcgStamp } from './util/practice_history';
import { setStateRewriter, useLayersStore, useProofreadingBackendStore } from './store';

export type PracticeKind = 'merge_then_cut' | 'cut';

export interface PracticeExample {
  id: string;
  title: string;
  kind: PracticeKind;
  dataset: string;
  pcg_server: string;
  pcg_table: string;
  state_url: string;
  supervoxel_a: string;
  supervoxel_b: string;
  root_a: string;
  root_b: string;
  /** JSON [x, y, z] in viewer voxels, from the registering admin's hover. */
  point_a?: string | null;
  point_b?: string | null;
  baseline_at: string;
  status: 'ready' | 'in_use' | 'needs_reset' | 'resetting' | 'broken';
  enabled: boolean;
  claimed_by: string | null;
  claim_nonce: string | null;
  claimed_at: string | null;
  expires_at: string | null;
  uses: number;
  reset_failures: number;
  last_reset_at: string | null;
  last_error: string | null;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function getViewer(): any {
  return (window as any)['viewer'];
}

// ─── PCG calls against the example's own server and table ──────────────────
// (pcg_service.ts reads the server from the viewer's current layer, which is
// not necessarily the example's dataset.)

function caveToken(server: string): string | null {
  return practiceToken(window.localStorage, server);
}

function pcgHeaders(server: string): HeadersInit {
  const token = caveToken(server);
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function pcgBase(ex: Pick<PracticeExample, 'pcg_server' | 'pcg_table'>) {
  return practiceBase(ex.pcg_server, ex.pcg_table);
}

export async function rootOfSupervoxel(ex: Pick<PracticeExample, 'pcg_server' | 'pcg_table'>, sv: string): Promise<string | null> {
  // Three tries: a fresh login can race the token, and the server rate limits.
  for (let attempt = 0; attempt < 3; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${pcgBase(ex)}/node/${sv}/root?int64_as_str=1`, { headers: pcgHeaders(ex.pcg_server), redirect: 'error', signal: AbortSignal.timeout(15000) });
    } catch (e) {
      // A timeout or a dropped connection: try again, never throw (a throw
      // here stopped the tutorial's watch for good).
      console.warn(`[practice] root of ${sv}: request failed`, e);
      await new Promise(r => setTimeout(r, 600 * (attempt + 1)));
      continue;
    }
    if (res.ok) {
      const data = await res.json();
      return data.root_id != null ? String(data.root_id) : null;
    }
    console.warn(`[practice] root of ${sv} on ${ex.pcg_table}: ${res.status} ${(await res.text()).slice(0, 200)}`);
    if (res.status === 401 || res.status === 403 || res.status === 404) break;
    await new Promise(r => setTimeout(r, 600 * (attempt + 1)));
  }
  return null;
}

/** PyChunkedGraph ids carry their layer in the top byte; supervoxels are layer 1. */
function pcgLayer(id: string): number {
  // Number() keeps 53 bits, plenty for the top byte of a 64-bit id.
  return Math.floor(Number(id) / 2 ** 56);
}

/** Walk a root down to one of its supervoxels (about eight calls). Works for
 *  old roots too, so a row registered by root id alone can be completed. */
export async function anySupervoxelOf(ex: Pick<PracticeExample, 'pcg_server' | 'pcg_table'>, rootId: string): Promise<string> {
  let id = rootId;
  for (let i = 0; i < 12 && pcgLayer(id) > 1; i++) {
    const res = await fetch(`${pcgBase(ex)}/node/${id}/children?int64_as_str=1`, { headers: pcgHeaders(ex.pcg_server), redirect: 'error', signal: AbortSignal.timeout(15000) });
    if (!res.ok) throw new Error(`children of ${id}: ${res.status}`);
    const data = await res.json();
    const kids: string[] = (data.children_ids ?? data.children ?? []).map(String);
    if (!kids.length) throw new Error(`node ${id} has no children`);
    id = kids[0];
  }
  if (pcgLayer(id) !== 1) throw new Error(`could not reach a supervoxel from ${rootId}`);
  return id;
}

/** Rows registered by root id alone get their supervoxels on first use. */
export async function ensureSupervoxels(ex: PracticeExample): Promise<PracticeExample> {
  if (ex.supervoxel_a && ex.supervoxel_b) return ex;
  const a = ex.supervoxel_a || await anySupervoxelOf(ex, ex.root_a);
  const b = ex.supervoxel_b || await anySupervoxelOf(ex, ex.root_b);
  ex.supervoxel_a = a;
  ex.supervoxel_b = b;
  return ex;
}

/** PyChunkedGraph timestamps arrive as epoch seconds, epoch milliseconds
 *  or "YYYY-MM-DD HH:MM:SS.ffffff" strings depending on the version; read
 *  them all. NaN means unparseable. */
interface LogOp { operationId: number; at: number }

/** Operations in a root's lineage made after `sinceIso`, newest first. */
async function opsSince(ex: PracticeExample, rootId: string, sinceIso: string): Promise<LogOp[]> {
  const res = await fetch(`${pcgBase(ex)}/root/${rootId}/tabular_change_log?filtered=false`, { headers: pcgHeaders(ex.pcg_server), redirect: 'error', signal: AbortSignal.timeout(15000) });
  if (!res.ok) throw new Error(`tabular_change_log ${res.status}`);
  const data = await res.json();
  return practiceOperationsAfter(data, rootId, sinceIso);
}

async function undoOp(ex: PracticeExample, operationId: number): Promise<void> {
  const res = await fetch(`${pcgBase(ex)}/undo?int64_as_str=1`, {
    method: 'POST', headers: pcgHeaders(ex.pcg_server), redirect: 'error', signal: AbortSignal.timeout(15000),
    body: JSON.stringify({ operation_id: operationId }),
  });
  if (!res.ok) throw new Error(`undo ${operationId}: ${res.status} ${(await res.text()).slice(0, 200)}`);
}

// ─── Viewer helpers ─────────────────────────────────────────────────────────

function segLayer(dataset: string): any {
  const viewer = getViewer();
  for (const ml of viewer?.layerManager?.managedLayers ?? []) {
    const url: string = ml.layer?.dataSources?.[0]?.spec?.url ?? '';
    if (ml.name === dataset || url.includes(`/table/${dataset}`)) return ml.layer;
  }
  return null;
}

/**
 * showOnly, and again a little later: a saved view can finish loading after
 * the first pass and bring its own extra segments back (Ames saw a stray
 * green cell from a registered view, 2026-10-05). Stops if another cell has
 * been shown since.
 */
function showOnlyAndKeep(ex: PracticeExample, rootIds: string[], colors: Array<[string, number]>) {
  const startA = session.rootA, startB = session.rootB;
  showOnly(ex.dataset, rootIds);
  colorSegments(ex.dataset, colors);
  for (const ms of [1500, 3500, 7000]) {
    setTimeout(() => {
      if (session.shownId !== ex.id) return;
      const layer = segLayer(ex.dataset);
      const set = layer?.displayState?.segmentationGroupState?.value?.visibleSegments;
      if (!set) return;
      const now = [...set].map((s: any) => s.toString());
      // A saved view that finishes loading late puts its own segments back,
      // under ids from before the last reset (Ames saw three blue segments,
      // 2026-10-08). Step in whenever the view is not what was set, unless
      // the learner has edited since: an edit changes the roots, and that
      // view is theirs to keep.
      const untouched = (session.rootA === startA && session.rootB === startB);
      const same = now.length === rootIds.length && rootIds.every(id => now.includes(id));
      if (untouched && !same) {
        showOnly(ex.dataset, rootIds);
        colorSegments(ex.dataset, colors);
      }
    }, ms);
  }
}

export function showOnly(dataset: string, rootIds: string[]) {
  const layer = segLayer(dataset);
  const set = layer?.displayState?.segmentationGroupState?.value?.visibleSegments
    ?? layer?.displayState?.rootSegments;
  if (!set) { console.warn('[practice] no segmentation layer for', dataset); return; }
  const current: any[] = [];
  for (const seg of set) current.push(seg);
  for (const seg of current) set.delete(seg);
  for (const id of rootIds) {
    try { set.add(Uint64.parseString(id)); } catch (e) { console.warn('[practice] bad root id', id); }
  }
}

/**
 * Undo every operation on either piece since the example's baseline, newest
 * first, with the signed-in user's token, and return the refreshed roots.
 * Throws if an undo fails or the pieces still share a root afterwards. Used
 * by the tutorial hand-back and by the admin "Reset now" button.
 */
async function undoSinceBaseline(ex: PracticeExample, assertLease: () => Promise<void>): Promise<{ a: string; b: string; undone: number }> {
  await assertLease();
  await ensureSupervoxels(ex);
  // Both lineages, since a cut can leave the pieces on different roots with
  // the merge in each history.
  // In passes, while a pass still changes something: a cut on a piece that
  // carries neither registered spot can be missing from these histories
  // until the cuts around it are undone. An operation can also stay listed
  // for good (undoing a cut whose edges are already back does nothing), so a
  // pass that finds the same operations as the last one is the end.
  let lastPass = '';
  let undoneCount = 0;
  for (let pass = 0; pass < 8; pass++) {
  const roots = new Set<string>();
  for (const sv of [ex.supervoxel_a, ex.supervoxel_b]) {
    const r = await rootOfSupervoxel(ex, sv);
    if (r) roots.add(r);
  }
  const seen = new Set<number>();
  const ops: LogOp[] = [];
  for (const r of roots) for (const op of await opsSince(ex, r, ex.baseline_at)) {
    if (!seen.has(op.operationId)) { seen.add(op.operationId); ops.push(op); }
  }
  if (ops.length > 10000) throw Error('Unexpectedly large practice history; ask an admin to review this example.');
  const details: Record<string, any> = {};
  for (let i = 0; i < ops.length; i += 100) {
    const ids = ops.slice(i, i + 100).map(op => op.operationId);
    const res = await fetch(`${pcgBase(ex)}/operation_details?int64_as_str=1&operation_ids=${encodeURIComponent(JSON.stringify(ids))}`, { headers: pcgHeaders(ex.pcg_server), redirect: 'error', signal: AbortSignal.timeout(15000) });
    if (!res.ok) throw Error(`operation_details ${res.status}`);
    Object.assign(details, await res.json());
  }
  const activeOps = remainingPracticeOperations(ops, details);
  for (const op of activeOps) { await assertLease(); await undoOp(ex, op.operationId); }
  const thisPass = activeOps.map(op => op.operationId).join(',');
  if (!activeOps.length || thisPass === lastPass) break;
  undoneCount += activeOps.length;
  lastPass = thisPass;
  }
  const activeOps = { length: undoneCount };
  await assertLease();
  // The spots the game itself checks: a cell whose own pair differs from them
  // was marked ready while it still read as solved (report 331).
  const [sa, sb] = checkSupervoxels(ex);
  const a = await rootOfSupervoxel(ex, sa);
  const b = await rootOfSupervoxel(ex, sb);
  if (!a || !b) throw new Error(`could not look up the roots of supervoxels ${sa} and ${sb} on ${ex.pcg_server} ${ex.pcg_table} (see the console for the server's answer)`);
  // A cut example starts fused; a merge example starts separate.
  const wantFused = ex.kind === 'cut';
  if ((a === b) !== wantFused) {
    const detail = `${activeOps.length} operation(s) after the baseline ${ex.baseline_at} were undone`;
    throw new Error(wantFused ? `after undo the pieces are still apart (${a}, ${b}); ${detail}`
                              : `after undo both pieces are still on root ${a}; ${detail}`);
  }
  return { a, b, undone: activeOps.length };
}

// ─── Viewer colours and tools ───────────────────────────────────────────────

/** Amy: the cell is purple, the loose piece yellow, and after the merge the
 *  whole thing is purple. Packed as 0xBBGGRR, the stated-colour format. */
const PURPLE = 0xff40a0 | 0; // rgb(160, 64, 255)
const YELLOW = 0x00d7ff | 0; // rgb(255, 215, 0)

export function colorSegments(dataset: string, colors: Array<[string, number]>) {
  const layer = segLayer(dataset);
  const map = layer?.displayState?.segmentationColorGroupState?.value?.segmentStatedColors;
  if (!map) return;
  for (const [id, packed] of colors) {
    try { setStatedColor(map, Uint64.parseString(id), packed); } catch (e) { console.warn('[practice] colour', id, e); }
  }
}

/** No practice cell: colour the first two visible segments of Amy's
 *  example so the copy ("yellow branch", "purple cell") still holds. */
export function colorFirstTwoVisible(dataset: string, attempt = 0) {
  const layer = segLayer(dataset);
  const set = layer?.displayState?.segmentationGroupState?.value?.visibleSegments;
  const ids: string[] = [];
  if (set) for (const seg of set) ids.push(seg.toString());
  // The saved view takes a moment to populate; keep trying for ten seconds.
  if (ids.length < 2 && attempt < 20) { setTimeout(() => colorFirstTwoVisible(dataset, attempt + 1), 500); if (!ids.length) return; }
  const colors: Array<[string, number]> = [];
  if (ids[0]) colors.push([ids[0], PURPLE]);
  if (ids[1]) colors.push([ids[1], YELLOW]);
  colorSegments(dataset, colors);
}

/** Activate the merge or cut tool if none is active, the way the command
 *  palette does: the segmentation layer gets selected and the tool's key is
 *  sent to the viewer. Used when a step says "press M" and the learner
 *  pressed next instead. */
export function ensureTool(tool: 'merge' | 'multicut', attempt = 0) {
  if (!session.example?.claim_nonce || !['merge', 'cut'].includes(session.phase)) return;
  const viewer = getViewer();
  if (!viewer) return;
  try {
    if (viewer.globalToolBinder?.activeTool_ || viewer.toolBinder?.activeTool_) return;
  } catch { /* check the DOM instead */ }
  if (document.querySelector('.neuroglancer-tool-status')) return;
  // A freshly loaded state can drop the tool once its layer finishes
  // loading, so keep trying for a few seconds until the tool bar is up.
  if (attempt < 8) setTimeout(() => ensureTool(tool, attempt + 1), 700);
  try {
    // By the class's static type: class names are renamed in production.
    const seg = viewer.layerManager?.managedLayers?.find((x: any) => (x.layer?.constructor as any)?.type === 'segmentation');
    // Selected, but its side panel is left shut: the tool needs the layer
    // chosen, not the panel open. Opening it here was what flashed a panel
    // on every box (Ames, 2026-10-12).
    if (seg) viewer.selectedLayer.layer = seg;
  } catch { /* non-critical */ }
  const key = tool === 'multicut' ? 'c' : 'm';
  const init: KeyboardEventInit = { key, code: key === 'c' ? 'KeyC' : 'KeyM', bubbles: true, cancelable: true };
  for (const el of [viewer.element, viewer.display?.container, document.getElementById('neuroglancer-container')]) {
    if (!el) continue;
    if (el instanceof HTMLElement) el.focus({ preventScroll: true });
    el.dispatchEvent(new KeyboardEvent('keydown', init));
  }
}

/**
 * Put the example's registered points into the merge tool as a finished
 * merge line, so a stuck learner only has to press Submit. The graphene
 * layer keeps merge lines as annotations in `mergeAnnotationState`; adding
 * one there is exactly what two Ctrl+clicks do. Points are in the viewer's
 * voxel space; the annotation layer shares it for these datasets.
 */
export function placeMergeLine(): boolean {
  const ex = session.example;
  if (!ex || !ex.point_a || !ex.point_b) return false;
  let pa: number[], pb: number[];
  try { pa = JSON.parse(ex.point_a); pb = JSON.parse(ex.point_b); } catch { return false; }
  if (!Array.isArray(pa) || !Array.isArray(pb) || pa.length < 3 || pb.length < 3) return false;
  const layer = segLayer(ex.dataset);
  const gc = layer?.graphConnection?.value;
  const source = gc?.mergeAnnotationState?.source;
  if (!source) { console.warn('[practice] no merge annotation source; is the merge tool on?'); return false; }
  const rootA = session.rootA || ex.root_a, rootB = session.rootB || ex.root_b;
  try {
    // The sink is the cell (B side of the line is the piece), matching what
    // the tool records from two clicks: [sinkRoot, sinkSupervoxel, sourceRoot, sourceSupervoxel].
    source.add({
      id: `nge-practice-${ex.id}`,
      type: 1, // AnnotationType.LINE
      pointA: Float32Array.from(pa.slice(0, 3)),
      pointB: Float32Array.from(pb.slice(0, 3)),
      relatedSegments: [[
        Uint64.parseString(rootA), Uint64.parseString(ex.supervoxel_a),
        Uint64.parseString(rootB), Uint64.parseString(ex.supervoxel_b),
      ]],
      properties: [],
    });
    return true;
  } catch (e) {
    console.warn('[practice] placing the merge line failed:', e);
    return false;
  }
}

// ─── Holding a cell: activity keeps it, silence hands it back ───────────────
// Amy: a cell is held while the learner is active; after a minute of
// silence a countdown shows, and at five minutes the cell is undone and
// released for the next person. The claim itself expires after the same five
// minutes, so a closed tab releases on the server side too.

export const HOLD_MINUTES = 5;
const WARN_AFTER_MS = 60 * 1000;
const RELEASE_AFTER_MS = HOLD_MINUTES * 60 * 1000;

let lastActivity = Date.now();
let lastHeartbeat = 0;
let activityTimer: ReturnType<typeof setInterval> | null = null;
let activityListening = false;

function noteActivity() {
  lastActivity = Date.now();
}

/** Seconds left before the cell is released, or null when not counting down. */
export function releaseCountdown(): number | null {
  if (!Object.keys(session.held).length) return null;
  const idle = Date.now() - lastActivity;
  if (idle < WARN_AFTER_MS) return null;
  return Math.max(0, Math.ceil((RELEASE_AFTER_MS - idle) / 1000));
}

function startActivityWatch() {
  lastActivity = Date.now();
  if (!activityListening) {
    activityListening = true;
    for (const ev of ['pointerdown', 'pointermove', 'keydown', 'wheel']) {
      window.addEventListener(ev, noteActivity, { passive: true, capture: true });
    }
  }
  if (activityTimer) return;
  activityTimer = setInterval(async () => {
    const cells = Object.values(session.held);
    if (!cells.length) { clearInterval(activityTimer!); activityTimer = null; return; }
    const idle = Date.now() - lastActivity;
    // Active: push every held claim's expiry along once a minute.
    if (idle < WARN_AFTER_MS && Date.now() - lastHeartbeat > 60 * 1000) {
      lastHeartbeat = Date.now();
      for (const ex of cells) {
        // Not while the cells are being handed back: the server has already
        // ended the session, and its refusal used to show "could not be
        // renewed" on the success box.
        if (session.releasing) break;
        practiceAction('heartbeat', { id: ex.id, session: ex.claim_nonce }).catch((error) => {
          const why = String(error?.message || error);
          console.warn('[practice] heartbeat failed:', why);
          if (session.releasing || !/has ended|no longer yours|not found/i.test(why)) return;
          for (const [slot, held] of Object.entries(session.held)) {
            if (held.id === ex.id && held.claim_nonce === ex.claim_nonce) delete session.held[slot];
          }
          if (session.example?.id === ex.id && session.example?.claim_nonce === ex.claim_nonce) practiceUnavailable();
        });
      }

    }
    document.dispatchEvent(new CustomEvent('nge:practice-countdown', { detail: { seconds: releaseCountdown() } }));
    if (idle >= RELEASE_AFTER_MS) {
      clearInterval(activityTimer!); activityTimer = null;
      session.phase = 'released';
      await endPractice();
      session.phase = 'released';
      document.dispatchEvent(new CustomEvent('nge:practice-released'));
    }
  }, 1000);
}

// ─── Waiting list ───────────────────────────────────────────────────────────
// When every cell of a kind is held, the learner joins the queue
// (tutorial_practice_waitlist). While the tutorial step is open the client
// checks every 20 s; the first in line whose turn comes gets the cell and a
// notification. Someone who leaves the step drops out of the queue.

let waitTimer: ReturnType<typeof setInterval> | null = null;

export async function joinWaitlist(kind: PracticeKind, onReady: (ex: PracticeExample) => void, onPosition: (n: number) => void) {
  const uid = userId();
  if (!uid) return;
  leaveWaitlist();
  const { error } = await supabase.from('tutorial_practice_waitlist')
    .upsert({ user_id: uid, kind, created_at: new Date().toISOString() }, { onConflict: 'user_id,kind' });
  if (error) { console.warn('[practice] waitlist join failed:', error.message); return; }
  const check = async () => {
    const { data } = await supabase.from('tutorial_practice_waitlist')
      .select('user_id').eq('kind', kind).order('created_at');
    const queue = (data ?? []).map((r: any) => r.user_id as string);
    const pos = queue.indexOf(uid);
    onPosition(pos < 0 ? 0 : pos + 1);
    if (pos !== 0) return; // not our turn yet
    const ex = await beginPractice(kind);
    if (ex) {
      leaveWaitlist();
      try {
        const { useProofreadingBackendStore } = await import('./store');
        await useProofreadingBackendStore().createSelfNotification({
          title: 'Your practice cell is ready',
          body: 'A cell freed up for the tutorial. Jump in, it is yours while you work; after five quiet minutes it goes to the next person in line.',
        });
      } catch { /* the status box says it too */ }
      onReady(ex);
    }
  };
  await check();
  waitTimer = setInterval(check, 20 * 1000);
}

/** How many cells of a kind exist, and how many could be claimed right now
 *  (ready, or held by this learner, or expired). Reads only. */
/**
 * How many cells of a kind can be used, are free, and are held by someone
 * else right now. A cell whose reset failed (or that was never set up) does
 * not count: it would block the tutorial for everyone while nobody is on it
 * (Ames, 2026-09-29: "in use" with no one using it).
 */
export async function practiceAvailability(kind: PracticeKind, only?: string): Promise<{ registered: number; free: number; heldByOthers: number }> {
  const uid = userId();
  const { data: everything, error } = await supabase.from('tutorial_practice_examples')
    .select('id,kind,status,claimed_by,expires_at,last_error,updated_at,supervoxel_a,supervoxel_b').eq('enabled', true);
  // Two pools (practice_pools.ts): a Merger Sandbox example is its own
  // one-cell pool (`only`); the tutorials draw on everything else.
  const all = everything?.filter((r: any) => only ? r.id === only : !SANDBOX_IDS.includes(r.id));
  if (error || !all) { console.warn('[practice] availability check failed:', error?.message); return { registered: 0, free: 0, heldByOthers: 0 }; }
  const now = Date.now();
  const live = (r: any) => r.status === 'in_use' && (!r.expires_at || Date.parse(r.expires_at) > now);
  const data = all.filter((r: any) => r.kind === kind);
  // Cells of the other tutorial on the same neuron: a reset of one undoes
  // edits on the other, so someone in the Cut tutorial holds the Merge
  // tutorial too, and the other way round (both use one neuron, 2026-09-29).
  const pieces = new Set(data.flatMap((r: any) => [r.supervoxel_a, r.supervoxel_b]).filter(Boolean));
  const neighbourHeld = all.filter((r: any) => r.kind !== kind && live(r) && r.claimed_by !== uid
    && [r.supervoxel_a, r.supervoxel_b].some((sv: string) => sv && pieces.has(sv))).length;
  const usable = data.filter((r: any) => r.status === 'ready' || r.status === 'in_use' || r.status === 'resetting'
    // A reset still pending (the job runs every 10 minutes), not a failed one.
    || (r.status === 'needs_reset' && !r.last_error && Date.parse(r.updated_at) > now - 15 * 60 * 1000));
  const free = usable.filter((r: any) => r.status === 'ready' || (r.status === 'in_use' && (r.claimed_by === uid || !live(r)))).length;
  const heldByOthers = usable.filter((r: any) => live(r) && r.claimed_by !== uid).length + neighbourHeld;
  // One learner at a time per tutorial (Ames, 2026-10-06): while anyone
  // else holds a cell of this kind, or a neighbouring one, nothing counts as
  // free, however many spare cells are in the pool. The pool is for
  // stand-ins when a staged cell is being reset, not for a second learner.
  return { registered: usable.length, free: heldByOthers ? 0 : free, heldByOthers };
}

/** Cells a tutorial needs before it starts: both of its practice cells when
 *  two are registered, otherwise whatever exists. */
export async function tutorialNeeds(kind: PracticeKind): Promise<{ needed: number; free: number; registered: number; heldByOthers: number }> {
  const a = await practiceAvailability(kind);
  return { needed: Math.min(2, Math.max(1, a.registered)), free: a.free, registered: a.registered, heldByOthers: a.heldByOthers };
}

let tutorialWaitTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Get in line for a whole tutorial (Amy): join the queue, watch every 20 s,
 * and when first in line with enough cells free, notify and call onReady.
 */
export async function waitForTutorial(kind: PracticeKind, onReady: () => void, onPosition: (n: number, needed: number) => void) {
  const uid = userId();
  if (!uid) return;
  stopWaitingForTutorial();
  const { error } = await supabase.from('tutorial_practice_waitlist')
    .upsert({ user_id: uid, kind, created_at: new Date().toISOString() }, { onConflict: 'user_id,kind' });
  if (error) console.warn('[practice] waitlist join failed:', error.message);
  const check = async () => {
    const { data } = await supabase.from('tutorial_practice_waitlist').select('user_id').eq('kind', kind).order('created_at');
    const queue = (data ?? []).map((r: any) => r.user_id as string);
    const pos = queue.indexOf(uid);
    const need = await tutorialNeeds(kind);
    onPosition(pos < 0 ? 1 : pos + 1, need.needed);
    if (pos > 0 || need.free < need.needed) return;
    stopWaitingForTutorial();
    try {
      const { useProofreadingBackendStore } = await import('./store');
      await useProofreadingBackendStore().createSelfNotification({
        title: kind === 'cut' ? 'The Cut tutorial is free' : 'The Merge tutorial is free',
        body: 'Your turn. Open the burger menu at the top right and start it; the practice cells are yours once you begin.',
      });
    } catch { /* the card says it too */ }
    onReady();
  };
  await check();
  if (!tutorialWaitTimer) tutorialWaitTimer = setInterval(check, 20 * 1000);
}

export function stopWaitingForTutorial() {
  if (tutorialWaitTimer) { clearInterval(tutorialWaitTimer); tutorialWaitTimer = null; }
  leaveWaitlist();
}

export function leaveWaitlist() {
  if (waitTimer) { clearInterval(waitTimer); waitTimer = null; }
  const uid = userId();
  if (!uid) return;
  supabase.from('tutorial_practice_waitlist').delete().eq('user_id', uid)
    .then(({ error }) => { if (error) console.warn('[practice] waitlist leave failed:', error.message); });
}

// ─── Session state ──────────────────────────────────────────────────────────

export type PracticePhase = 'none' | 'claiming' | 'merge' | 'cut' | 'busy' | 'unavailable' | 'done' | 'released';

const session = {
  /** The cell the current step works on. */
  example: null as PracticeExample | null,
  /** Every cell this learner holds, by slot ('a' for the first practice
   *  step of a tutorial, 'b' for the second). All go back together at the
   *  end, so one learner runs the whole tutorial on cells nobody else can
   *  take (Amy: one person at a time, and the merge section has two parts). */
  held: {} as Record<string, PracticeExample>,
  /** Example whose saved view is currently loaded, so later steps do not
   *  reload it (a reload drops the active tool and the colours). */
  shownId: '',
  rootA: '',
  rootB: '',
  phase: 'none' as PracticePhase,
  releasing: null as Promise<void> | null,
};

export function currentPractice() {
  return session;
}

function pausePracticeTools() {
  const viewer = getViewer();
  viewer?.globalToolBinder?.activeTool_?.cancel?.();
  viewer?.toolBinder?.activeTool_?.cancel?.();
}

function practiceUnavailable() {
  session.example = null;
  session.shownId = '';
  session.phase = 'unavailable';
  pausePracticeTools();
  document.dispatchEvent(new CustomEvent('nge:practice-unavailable'));
}

function userId(): string | null {
  try { return useProofreadingBackendStore().userId; } catch { return null; }
}

/**
 * Claim an example of the given kind for this user and put the viewer on
 * it: load the saved view, then show only the two pieces at their current
 * root ids. A held example of that kind is reused; a held example of the
 * other kind is handed back first.
 * Returns null when nobody is logged in or every example is busy.
 */
/**
 * For a cut example, `root_a` and `root_b` are the two pieces as they were
 * after Amy's cut (the row is registered from them and resets leave them
 * alone), while the cell at rest is the fused root. 'preview' shows those
 * two pieces in yellow and purple, the result the learner is about to
 * reproduce; 'start' shows the fused root for them to cut.
 */
export type PracticeView = 'start' | 'preview';

/**
 * Whether a cell's two spots are on different segments right now, asked
 * directly. Used where it matters that a cut is credited even if the watch
 * missed it (the Merger Sandbox count). null when it cannot be told.
 */
export async function cellIsCut(id: string): Promise<boolean | null> {
  try {
    let ex = [...Object.values(session.held), ...Object.values(riders)].find(c => c.id === id) as PracticeExample | undefined;
    if (!ex) {
      // No longer held (the session was lost): the row still says where to look.
      const { data } = await supabase.from('tutorial_practice_examples').select('*').eq('id', id).limit(1);
      ex = data?.[0] as PracticeExample | undefined;
    }
    if (!ex) return null;
    const [sa, sb] = checkSupervoxels(ex);
    const [a, b] = await Promise.all([rootOfSupervoxel(ex, sa), rootOfSupervoxel(ex, sb)]);
    if (!a || !b) return null;
    // Parted, and nothing left behind that should have come off too.
    return a !== b && !(LEFTOVER[ex.id] && await practiceLeftover(ex.id));
  } catch { return null; }
}

/** Whether a cut cell has a finished cut to preview (its two roots from
 *  after the cut). Cells registered fused do not. */
export function hasCutPreview(ex: PracticeExample | null): boolean {
  return !!ex && ex.kind === 'cut' && !!ex.root_a && ex.root_a !== ex.root_b;
}

/**
 * Exercises that ride on another cell's claim.
 *
 * The Merge tutorial's two merges are on one neuron. The server will not
 * hand a learner a second cell on a neuron while the first is held (its rule
 * against two people on one neuron also catches the learner's own cell), so
 * the second merge was skipped on every run from 2026-10-05 to 2026-10-08:
 * the axon cell's use count never moved. It does not need a claim of its
 * own. Nobody else can start the Merge tutorial while the first cell is
 * held, and resetting the first cell undoes every edit on the neuron since
 * the shared baseline, the second merge included. So the second exercise is
 * shown from its row, read only, and rides on the first cell's claim.
 */
const RIDES_WITH: Record<string, string> = {
  // Axon missing a branch rides with Branch cut in half.
  'a4bd2f76-67e9-4093-adc7-e670230d1577': 'b231f4e7-e9f3-4214-941f-975b8b25a237',
};
const riders: Record<string, PracticeExample> = {};

async function riderFor(slot: string, kind: PracticeKind): Promise<PracticeExample | null> {
  const hosts = Object.values(session.held);
  for (const [riderId, hostId] of Object.entries(RIDES_WITH)) {
    const host = hosts.find(h => h.id === hostId);
    if (!host || Object.values(riders).some(r => r.id === riderId)) continue;
    const { data, error } = await supabase.from('tutorial_practice_examples').select('*').eq('id', riderId).eq('enabled', true).eq('kind', kind).limit(1);
    const row = (data ?? [])[0] as PracticeExample | undefined;
    if (error || !row) { console.warn('[practice] rider cell not available:', riderId, error?.message); continue; }
    // The host's claim stands in for its own (never sent to the server).
    riders[slot] = { ...row, claimed_by: host.claimed_by, claim_nonce: host.claim_nonce };
    console.info(`[practice] ${row.title} rides on the claim of ${host.title}`);
    return riders[slot];
  }
  return null;
}

/** The cells this learner holds right now, riders included. */
export function heldPracticeIds(): string[] {
  return [...Object.values(session.held), ...Object.values(riders)].map(ex => ex.id);
}

/** Whether this learner holds a cell in the slot. */
export function holdsSlot(slot: string): boolean {
  return !!session.held[slot] || !!riders[slot];
}

/** True once a practice view is on screen (no need to announce the move). */
export function practiceShown(): boolean {
  return !!session.shownId;
}

/** Claim (or keep) a practice cell in a slot and show it. `show: false`
 *  only claims, for a slot the tutorial will show later (Merge step 3 takes
 *  both cells up front; loading the second view there cost seconds). */
export async function beginPractice(kind: PracticeKind = 'merge_then_cut', view: PracticeView = 'start', opts: { slot?: string; show?: boolean; avoid?: string[]; prefer?: string; only?: string } = {}): Promise<PracticeExample | null> {
  const slot = opts.slot ?? 'a';
  const show = opts.show !== false;
  // Right after a reload the login is still settling; give it a few seconds
  // before deciding the learner is signed out (Amy saw "you need to be
  // signed in" while signed in).
  let uid = userId();
  for (let i = 0; !uid && i < 12; i++) { await new Promise(r => setTimeout(r, 500)); uid = userId(); }
  if (!uid) { practiceUnavailable(); return null; }
  // A rider has no claim to renew: show it if its host is still held.
  const riding = riders[slot];
  if (riding && riding.kind === kind) {
    if (!Object.values(session.held).some(h => h.id === RIDES_WITH[riding.id])) { delete riders[slot]; }
    else {
      if (!show) return riding;
      session.example = riding;
      await showExample(riding, view);
      session.phase = kind === 'cut' ? 'cut' : 'merge';
      resumeTool();
      startActivityWatch();
      return riding;
    }
  }
  const held = session.held[slot];
  if (held && held.claimed_by === uid && held.kind === kind) {
    try { await practiceAction('heartbeat', { id: held.id, session: held.claim_nonce }); }
    catch (error: any) {
      const why = String(error?.message || error);
      console.warn(`[practice] renewing slot ${slot} failed:`, why);
      // Only a session the server says is over ends practice. Any other
      // failure (a dropped request, a busy server) keeps the cell: the next
      // renewal, a minute on, tries again.
      if (/has ended|no longer yours|not found/i.test(why)) { delete session.held[slot]; practiceUnavailable(); return null; }
    }
    if (!show) return held;
    session.example = held;
    await showExample(held, view);
    session.phase = kind === 'cut' ? 'cut' : 'merge';
    resumeTool();
    startActivityWatch();
    return held;

  }
  // A different kind in this slot (merge tutorial, then cut tutorial): put
  // everything back first.
  if (Object.values(session.held).some(ex => ex.kind !== kind)) await endPractice();
  session.phase = 'claiming';
  pausePracticeTools();
  // One learner at a time: a first claim is refused while someone else is in
  // this tutorial. The gate at the start checks the same thing; this covers
  // a learner who got past it (a resumed tutorial, or two starts at once).
  if (!Object.keys(session.held).length) {
    const a = await practiceAvailability(kind, opts.only);
    if (a.heldByOthers > 0) { session.phase = 'busy'; return null; }
  }
  // `avoid`: cells this step would rather not get (a tutorial that wants
  // its cells in a set order asks for the first while avoiding the second).
  // A tutorial never draws on a Merger Sandbox cell; a sandbox example
  // (`only`) takes its own cell or nothing.
  const exclude = [...Object.values(session.held).map(ex => ex.id), ...(opts.avoid ?? []), ...(opts.only ? [] : SANDBOX_IDS)];
  // Never hand out a cell registered wrong (Celia's Test cell holds segment
  // ids where supervoxels belong, so nothing can tell when it is merged).
  try {
    const { data } = await supabase.from('tutorial_practice_examples')
      .select('id,status,supervoxel_a,supervoxel_b').eq('enabled', true).eq('kind', kind);
    // `prefer`: a staged tutorial names the cell for this step. When that
    // cell is free, every other one is excluded so the server hands it out;
    // when it is not, the claim falls back to whatever is free.
    if (opts.only) {
      const mine = ((data ?? []) as any[]).find(r => r.id === opts.only);
      if (!mine || mine.status !== 'ready') { session.phase = 'busy'; return null; }
      for (const r of (data ?? []) as any[]) if (r.id !== opts.only && !exclude.includes(r.id)) exclude.push(r.id);
    }
    const want = opts.prefer && !exclude.includes(opts.prefer)
      ? ((data ?? []) as any[]).find(r => r.id === opts.prefer && r.status === 'ready') : null;
    if (want) for (const r of (data ?? []) as any[]) if (r.id !== want.id && !exclude.includes(r.id)) exclude.push(r.id);
    for (const r of (data ?? []) as any[]) {
      const bad = [r.supervoxel_a, r.supervoxel_b].some((sv: string) => sv && pcgLayer(sv) !== 1)
        || (r.supervoxel_a && r.supervoxel_a === r.supervoxel_b);
      if (bad && !exclude.includes(r.id)) exclude.push(r.id);
    }
  } catch {
    // Without the list, a sandbox claim could be handed some other cell.
    if (opts.only) { session.phase = 'busy'; return null; }
  }
  let row: PracticeExample | null;
  try { row = await practiceAction('claim', { kind, exclude }); }
  catch (error: any) {
    console.warn(`[practice] claim ${kind} slot ${slot} failed:`, error?.message || error);
    // A failed claim for a further cell must not throw away the one already
    // held and on screen (Ames saw "practice cells need you to be signed
    // in" over her own loaded cell, 2026-10-09). Treat it as "no cell of its
    // own", which lets a rider take the slot; only a first claim that fails
    // means practice is unavailable.
    if (!Object.keys(session.held).length) { practiceUnavailable(); return null; }
    row = null;
  }
  // A claim that hands back a cell already held (the database still has
  // the claim function without p_exclude) counts as nothing free: a
  // practice step never shows the previous step's cell again (Amy).
  console.info(`[practice] claim ${kind} slot ${slot} excluding [${exclude.join(', ')}] returned`, row ? `${row.id} (${row.title})` : 'nothing');
  if (row && exclude.includes(row.id)) row = null;
  // A second cell that could not be had (show: false) must not mark the
  // session busy: the first cell is on screen and usable (Ames saw "every
  // practice cell is in use" over her own loaded cell, 2026-10-05).
  if (!row) {
    // No cell of its own: a rider, if this slot's exercise shares a neuron
    // with a cell already held.
    const rider = await riderFor(slot, kind);
    if (rider) {
      if (session.example) session.phase = kind === 'cut' ? 'cut' : 'merge';
      if (!show) return rider;
      session.example = rider;
      await showExample(rider, view);
      session.phase = kind === 'cut' ? 'cut' : 'merge';
      resumeTool();
      startActivityWatch();
      return rider;
    }
    if (show) session.phase = 'busy'; else if (session.example) session.phase = kind === 'cut' ? 'cut' : 'merge';
    return null;
  }
  session.held[slot] = row;
  if (!show) {
    // Claimed, not shown: the step's own cell stays on screen.
    if (session.example) session.phase = kind === 'cut' ? 'cut' : 'merge';
    startActivityWatch();
    return row;
  }

  session.example = row;
  await showExample(row, view);
  session.phase = kind === 'cut' ? 'cut' : 'merge';
  resumeTool();
  startActivityWatch();
  return row;
}

/** Every browser undo obtains a fresh server lease and uses its saved geometry.
 * A paused tab must revalidate before each operation; it cannot resume a reset
 * after the scheduled worker or another learner has taken over. */
export async function resetPracticeExample(exampleId: string, sessionNonce?: string | null): Promise<{a: string; b: string; undone: number}> {
  const ex = await practiceAction('begin_reset', { id: exampleId, session: sessionNonce });
  const nonce = ex.reset_nonce;
  const started = Date.now();
  const assertLease = async () => {
    if (Date.now() - started > 120000) throw new Error('Reset timed out. The scheduled reset will finish it.');
    await practiceAction('check_reset', { id: ex.id, nonce });
  };
  try {
    const result = await undoSinceBaseline(ex, assertLease);
    await practiceAction('finish_reset', { id: ex.id, nonce, clean: true, root_a: result.a, root_b: result.b });
    return result;
  } catch (error: any) {
    await practiceAction('finish_reset', { id: ex.id, nonce, clean: false, error: String(error?.message || error) }).catch(() => {});
    throw error;
  }
}

let resumeToolAfterLoad: 'merge' | 'multicut' | null = null;

function resumeTool() {
  const tool = resumeToolAfterLoad;
  resumeToolAfterLoad = null;
  if (tool) setTimeout(() => ensureTool(tool), 600);
}

/** For the tutorials: whether the merge or cut tool is on right now. */
export function practiceToolOn(): boolean { return toolActive(); }

/** True while a tool (merge, cut) is switched on. */
function toolActive(): boolean {
  const viewer = getViewer();
  try { if (viewer?.globalToolBinder?.activeTool_ || viewer?.toolBinder?.activeTool_) return true; } catch { /* DOM check */ }
  return !!document.querySelector('.neuroglancer-tool-status');
}

/**
 * The two spots that decide whether an exercise is done, when they are not
 * the row's own. The row's supervoxels are what the reset job is pinned to
 * (its manifest must match the row exactly), and for the 2D cut cell they
 * were arbitrary spots in each neuron: a correct cut near the join left one
 * of them on the wrong side, so the cut never counted (Ames, 2026-10-07).
 * These are the supervoxels under her own two points beside the join. Kept
 * here so the fix needs no database change.
 */
const CHECK_SUPERVOXELS: Record<string, [string, string]> = {
  // Axon fused to a dendrite, 2D cut: axon (86830, 59380, 846), dendrite (86418, 59244, 814)
  '0813e168-d4e6-4baa-91b7-c9846b9fc6f4': ['75507587050992924', '75507587050897753'],
  // Merger Sandbox 6 (Celia's "Small branch merger on cell"): both of the
  // row's points are on the main cell, so no cut could ever count. These are
  // the two supervoxels either side of Ames's own cut of 2026-10-11
  // (operation 1871): the small branch, and the cell beside it.
  'c04eed2c-4204-4b3d-a25d-2f7aa880c1aa': ['75719174185485322', '75719174185482374'],
  // Merger Sandbox 7 and 8: the row's points are micrometres apart, so a cut
  // well away from the join still parted them and counted (Ames's test cut
  // on Merger 8, operation 1893, 2026-10-11). These are two supervoxels that
  // touch across the join, from the edges her reference cuts removed
  // (operations 1872 and 1873). Checked against history: parted by the
  // reference cut, still together after the stray one.
  // Widened 2026-10-12: the pair that touched across the join was too
  // strict. A cut with its points a few voxels over takes a slightly
  // different plane, and a player made the right cut on Merger 8 several
  // times without it counting (AzureJay, operation 1966). These are the
  // supervoxels under the reference cut's own red and blue points, about
  // 300 nm apart with the join between them. Replayed against every cut in
  // the history: the cuts at the join part them, the stray ones do not.
  '0482d846-0c16-4393-ab8a-0d1212b9520f': ['75508549324873021', '75508549324875952'],
  '814be870-9149-4474-876f-195e0af0bd08': ['74593068321697409', '74593068321720790'],
};
/**
 * Split view (2D images beside 3D), with the 2D panel rebuilt once the view
 * has settled.
 *
 * A 2D panel opened in the same moment a saved view loads can stay blocky:
 * the images sit at a coarse level and never sharpen. Testers found that
 * pressing Space twice (3D only, then split again) fixes it (andrearwen,
 * 2026-10-08; Ames saw the same in the Merger Sandbox). This does that for
 * them: a moment after opening, the panel is taken down and put straight
 * back, in one go so nothing flickers.
 */
let splitRebuildTimer: ReturnType<typeof setTimeout> | null = null;
export function openSplitView() {
  const viewer = getViewer();
  try { viewer?.layout?.restoreState('xy-3d'); } catch (e) { console.warn('[practice] could not open the 2D view:', e); return; }
  if (splitRebuildTimer) clearTimeout(splitRebuildTimer);
  splitRebuildTimer = setTimeout(() => {
    splitRebuildTimer = null;
    try {
      const v = getViewer();
      // Only if the learner has not changed the layout themselves meanwhile.
      if (JSON.stringify(v?.layout?.toJSON?.()) !== '"xy-3d"') return;
      v.layout.restoreState('3d');
      v.layout.restoreState('xy-3d');
      v.display?.onResize?.();
      v.display?.scheduleRedraw?.();
    } catch (e) { console.warn('[practice] could not rebuild the 2D view:', e); }
  }, 2500);
}

/**
 * How a staged tutorial cell opens, when that differs from its registered
 * view: the saved view to load and the panel layout. Kept here so a new view
 * needs no database change.
 */
const STAGED_VIEW: Record<string, { state?: string; layout?: '3d' | 'xy-3d' }> = {
  // First merge (dendrite): 3D only, the merge point centred, no stray
  // segments (Ames's view, 2026-10-08).
  'b231f4e7-e9f3-4214-941f-975b8b25a237': { state: 'middleauth+https://global.brain-wire-test.org/nglstate/api/v1/4805358249836544', layout: '3d' },
  // Merger Sandbox 5: Ames's view (2026-10-09), the registered one showed
  // the wrong cell.
  'f11ad22b-8043-4223-9b7e-264eb5d6d0a3': { state: 'middleauth+https://global.brain-wire-test.org/nglstate/api/v1/5699422331600896' },
  // Second merge (axon): done in 2D, so split view.
  'a4bd2f76-67e9-4093-adc7-e670230d1577': { layout: 'xy-3d' },
};

function checkSupervoxels(ex: PracticeExample): [string, string] {
  return CHECK_SUPERVOXELS[ex.id] ?? [ex.supervoxel_a, ex.supervoxel_b];
}

async function showExample(ex: PracticeExample, view: PracticeView = 'start') {
  if (session.shownId !== ex.id) {
    // Loading a view rebuilds the layers, and a tool left on stays bound to
    // the old one: it looks on but does nothing (Ames had to leave and
    // re-enter merge mode at step 6). Switch it off here and back on after.
    const wasOn = toolActive();
    pausePracticeTools();
    const staged = STAGED_VIEW[ex.id];
    // The saved views were made with neuroglancer's side panels open. Shut
    // them in the view itself before it loads, so they never flash open and
    // closed in front of the learner (Ames, 2026-10-11).
    // Likewise the layout: a cell shown in 3D loads in 3D, so the 2D panels
    // are never built and torn down (Merger Sandbox cells, and any cell
    // staged as 3D).
    const want3d = SANDBOX_IDS.includes(ex.id) || staged?.layout === '3d';
    setStateRewriter(async (state: any) => {
      if (state?.selectedLayer) state.selectedLayer = { ...state.selectedLayer, visible: false };
      if (state?.selection) state.selection = { ...state.selection, visible: false };
      if (want3d && state) state.layout = '3d';
      // Annotation layers saved in the view (the "remove" and "keep" points
      // a view was made with) are left out: the tutorial draws its own hint
      // gems, and both together showed every point twice, with two extra
      // tabs (Ames, 2026-10-13). The hints are read from the views
      // separately, so nothing is lost.
      if (Array.isArray(state?.layers)) state.layers = state.layers.filter((l: any) => l?.type !== 'annotation');
      return state;
    });
    try { await useLayersStore().loadState(staged?.state ?? ex.state_url); } finally { setStateRewriter(null); }
    // The staged panel layout for this cell (3D only, or split with 2D).
    if (staged?.layout === 'xy-3d') openSplitView();
    else if (staged?.layout) {
      try { getViewer()?.layout?.restoreState(staged.layout); } catch (e) { /* the view still loads */ }
    }
    // Back on once the practice phase is set (beginPractice does it), so
    // ensureTool's guard lets it through.
    if (wasOn) resumeToolAfterLoad = ex.kind === 'cut' ? 'multicut' : 'merge';
    // restoreState applies asynchronously; give the layer a moment to exist.
    await new Promise(r => setTimeout(r, 800));
    // Full detail for the 2D images. A saved view can carry a coarse image
    // setting, which draws the images as big grey blocks and counts as fully
    // loaded, so no loading sign shows either (Ames, 2026-10-07). Logged so a
    // blocky image can be traced: what the view asked for, and its zoom.
    try {
      const viewer = getViewer();
      for (const ml of viewer?.layerManager?.managedLayers ?? []) {
        if ((ml.layer?.constructor as any)?.type !== 'image') continue;
        const t = ml.layer.sliceViewRenderScaleTarget;
        console.info(`[practice] image layer "${ml.name}": detail setting ${t?.value}, 2D zoom ${viewer?.navigationState?.zoomFactor?.value}`);
        if (t && t.value > 1) t.value = 1;
      }
    } catch (e) { /* the view still loads */ }
    session.shownId = ex.id;
  }
  await ensureSupervoxels(ex);
  const [sa, sb] = checkSupervoxels(ex);
  const [a, b] = await Promise.all([rootOfSupervoxel(ex, sa), rootOfSupervoxel(ex, sb)]);
  session.rootA = a ?? ex.root_a;
  session.rootB = b ?? ex.root_b;
  // A preview needs the two roots from after a cut. A cell registered from
  // its fused state has never been cut, so there is nothing finished to
  // show: it gets the normal fused view (hasCutPreview tells the step).
  if (view === 'preview' && ex.kind === 'cut' && ex.root_a !== ex.root_b) {
    // The finished cut: piece yellow, cell purple. Old roots still render.
    showOnlyAndKeep(ex, [ex.root_b, ex.root_a], [[ex.root_b, PURPLE], [ex.root_a, YELLOW]]);
    return;
  }
  // A cell missing pieces (Extension Sandbox) opens on the cell alone:
  // finding where it stops and what belongs there is the exercise. Every
  // extension cell, one piece or several (Ames, 2026-10-24: the one-piece
  // cell opened with its piece already showing, "half way complete").
  if (CHECK_GROUPS[ex.id] || EXTENSION_CELLS.some(c => c.id === ex.id)) {
    showOnlyAndKeep(ex, [session.rootA], [[session.rootA, PURPLE]]);
    return;
  }
  // Merge example: cell purple, loose piece yellow. Cut example: the fused
  // segment purple, so the piece cut off it stands out in its own colour.
  showOnlyAndKeep(ex, session.rootA === session.rootB ? [session.rootA] : [session.rootA, session.rootB],
    session.rootA === session.rootB
      ? [[session.rootA, PURPLE]]
      : [[session.rootA, PURPLE], [session.rootB, YELLOW]]);
}

/**
 * A cut that takes off only part of what does not belong.
 *
 * The 2D cut cell's axon can be cut partway along, which parts the two
 * checked spots and used to count, with half the axon still on the dendrite
 * (Ames, 2026-10-13). `pair` is a spot on that remaining half and a spot on
 * the dendrite: while they share a segment, the job is half done. The red and
 * blue points (viewer voxels) show where the rest of the cut goes.
 */
const LEFTOVER: Record<string, { pair: [string, string]; message: string; red: number[][]; blue: number[][] }> = {
  // First cut (fusion on a proofread cell). The axon touches the dendrite
  // along a stretch, and a cut placed at one end takes only the near half
  // off. The pair is the supervoxel under the far red hint point and the one
  // under the far blue hint point. Replayed against all 19 cuts in the
  // cell's history: the five that took the whole axon part them, the
  // fourteen that left half do not.
  '02c5adcc-23c8-4003-83cd-cd9df7a65ce0': {
    pair: ['75155055934384400', '75155055934269534'],
    message: 'Good cut, but you cut off only half of the merger. The rest of the axon is still on the dendrite. Go back and get it: the gems show where.',
    red: [[82030, 49104, 529]],
    blue: [[81910, 49234, 521]],
  },
  // Axon fused to a dendrite, 2D cut. Supervoxels under (86309, 59613, 786)
  // and (86358, 59125, 806).
  '0813e168-d4e6-4baa-91b7-c9846b9fc6f4': {
    pair: ['75437218239981902', '75437149587202747'],
    message: 'Good cut, but you cut off only half of the merger. Part of the axon is still on the dendrite. Go back and get the rest: the gems show where.',
    red: [[86385, 59535, 793]],
    blue: [[86436, 59597, 806]],
  },
};

/** For the cell on screen: what is still attached that should not be, or
 *  null when there is nothing (or it cannot be told). */
export async function practiceLeftover(id?: string): Promise<{ message: string; red: number[][]; blue: number[][] } | null> {
  try {
    const ex = id ? [...Object.values(session.held), ...Object.values(riders)].find(c => c.id === id) : session.example;
    const left = ex ? LEFTOVER[ex.id] : undefined;
    if (!ex || !left) return null;
    const [a, b] = await Promise.all([rootOfSupervoxel(ex, left.pair[0]), rootOfSupervoxel(ex, left.pair[1])]);
    return a && b && a === b ? left : null;
  } catch { return null; }
}

/**
 * A cell missing more than one piece (Extension Sandbox, Ames 2026-10-24).
 * The first supervoxel is on the cell, the rest one on each missing piece.
 * Merged means every one of them is on the cell's segment. The row's own
 * pair (supervoxel_a and _b) is the cell and the first piece, so the reset
 * job's start check still holds.
 */
const CHECK_GROUPS: Record<string, string[]> = {
  // The cell (58256, 36905, 1161), its pieces (77321, 49937, 213) and
  // (87334, 52032, 994).
  '63a057ed-ce08-4a6f-915d-eca29214e48c': ['73535750519863797', '74873580823075327', '75507037362320389'],
  // Extension Sandbox cell 2: the cell (62612, 39202, 558), its pieces
  // (62448, 39130, 537) and (61025, 38968, 394).
  'f3dfed18-9592-46ed-8ca7-90310aea0d60': ['73817362666812872', '73817362666662242', '73746993788759465'],
};

/** For a cell with a group: how many of its missing pieces are back on. */
export async function groupProgress(id?: string): Promise<{ joined: number; total: number } | null> {
  try {
    const ex = id ? [...Object.values(session.held), ...Object.values(riders)].find(c => c.id === id) : session.example;
    const group = ex ? CHECK_GROUPS[ex.id] : undefined;
    if (!ex || !group) return null;
    const roots = await Promise.all(group.map(sv => rootOfSupervoxel(ex, sv)));
    if (roots.some(r => !r)) return null;
    return { joined: roots.slice(1).filter(r => r === roots[0]).length, total: group.length - 1 };
  } catch { return null; }
}

/** Whether a merge cell is finished: every piece on the cell's segment.
 *  Asked directly, for the sandbox's check box. null when it cannot be told. */
export async function cellIsJoined(id: string): Promise<boolean | null> {
  try {
    let ex = [...Object.values(session.held), ...Object.values(riders)].find(c => c.id === id) as PracticeExample | undefined;
    if (!ex) {
      const { data } = await supabase.from('tutorial_practice_examples').select('*').eq('id', id).limit(1);
      ex = data?.[0] as PracticeExample | undefined;
    }
    if (!ex) return null;
    const group = CHECK_GROUPS[ex.id] ?? checkSupervoxels(ex);
    const roots = await Promise.all(group.map(sv => rootOfSupervoxel(ex!, sv)));
    if (roots.some(r => !r)) return null;
    return roots.every(r => r === roots[0]);
  } catch { return null; }
}

/**
 * Help on an extension cell: show the missing pieces too, in yellow. The
 * cell opens alone, so without this the hint gems sat over empty space
 * (Ames, 2026-10-28: "a help point floating in space").
 */
export async function revealMissingPieces(): Promise<void> {
  const ex = session.example;
  if (!ex || !EXTENSION_CELLS.some(c => c.id === ex.id)) return;
  const pieces = (CHECK_GROUPS[ex.id] ?? [ex.supervoxel_a, ex.supervoxel_b]).slice(1);
  const cellRoot = await rootOfSupervoxel(ex, CHECK_GROUPS[ex.id]?.[0] ?? ex.supervoxel_a);
  const roots = (await Promise.all(pieces.map(sv => rootOfSupervoxel(ex, sv)))).filter((r): r is string => !!r && r !== cellRoot);
  if (!roots.length) return;
  const layer = segLayer(ex.dataset);
  const set = layer?.displayState?.segmentationGroupState?.value?.visibleSegments ?? layer?.displayState?.rootSegments;
  if (!set) return;
  for (const r of roots) { try { set.add(Uint64.parseString(r)); } catch { /* skip a bad id */ } }
  colorSegments(ex.dataset, roots.map(r => [r, YELLOW] as [string, number]));
}

/** True when the two pieces currently share a root. */
export async function piecesMerged(): Promise<boolean | null> {
  const ex = session.example;
  if (!ex) return null;
  await ensureSupervoxels(ex);
  const [sa, sb] = checkSupervoxels(ex);
  const [a, b] = await Promise.all([rootOfSupervoxel(ex, sa), rootOfSupervoxel(ex, sb)]);
  if (!a || !b) return null;
  // A cell with several missing pieces is merged only when all are on.
  const group = CHECK_GROUPS[ex.id];
  if (group) {
    const roots = await Promise.all(group.slice(2).map(sv => rootOfSupervoxel(ex, sv)));
    if (roots.some(r => !r)) return null;
    if (a === b && roots.some(r => r !== a)) {
      session.rootA = a; session.rootB = b;
      return false;
    }
  }
  const changed = a !== session.rootA || b !== session.rootB;
  session.rootA = a;
  session.rootB = b;
  if (changed) {
    // New roots after an edit: keep the story's colours on them.
    colorSegments(ex.dataset, a === b ? [[a, PURPLE]] : [[a, PURPLE], [b, YELLOW]]);
  }
  return a === b;
}

/**
 * Undo everything done to the example since its baseline and hand it back.
 * Safe to call more than once; later calls wait on the first.
 */
export function endPractice(): Promise<void> {
  if (session.releasing) return session.releasing;
  const cells = Object.values(session.held);
  const uid = userId();
  if (!cells.length || !uid) { session.phase = 'none'; return Promise.resolve(); }
  session.releasing = (async () => {
    for (const ex of cells) {
      try { await resetPracticeExample(ex.id, ex.claim_nonce); }
      catch (error: any) { console.warn('[practice] reset left to the scheduled worker:', error?.message || error); }
    }
    session.held = {};
    for (const k of Object.keys(riders)) delete riders[k];
    session.example = null;
    session.shownId = '';
    session.rootA = '';
    session.rootB = '';
    session.phase = 'done';
    session.releasing = null;
  })();
  return session.releasing;
}
